import { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMyQualityMetrics } from 'shared/hooks/knowledge-dashboard.hooks';
import { isQualityMetricsEnabled } from 'shared/store/selectors/features.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Divider } from 'src/components/design-system';
import styles from './quality-metrics-widget.module.scss';
import { Link } from 'react-router-dom';
import { QUALITY_METRICS_NAVIGATION_SOURCE, ROUTES, EVENTS } from 'shared/constants';
import {
  QualityMetricBox,
  getMetricAriaLabel,
} from 'src/components/quality-metric/quality-metric-box/quality-metric-box';
import { useAnalyticsImpression } from 'src/hooks/analytics/use-analytics-impression';
import { deriveMetricsTrends } from 'shared/utils/quality-metrics.utils';
import { GridItemsNavigator } from 'src/components/common/items-navigator';
import { Tooltip } from 'src/components/core';

function WidgetMetricBox({ metric, trend }) {
  const lang = useSelector(getLang('QUALITY_METRIC'));

  const TitleWrapper = useCallback(
    ({ children }) => (
      <Tooltip title={metric.title} announceOnOpen={false}>
        {children}
      </Tooltip>
    ),
    [metric.title],
  );

  return (
    <Link
      className={styles.metric}
      to={`${ROUTES.QUALITY_METRIC}/${metric.metricId}`}
      state={{ source: QUALITY_METRICS_NAVIGATION_SOURCE.QUALITY_METRIC_WIDGET }}
      aria-label={getMetricAriaLabel({ metric, trend, lang })}>
      <QualityMetricBox
        metric={metric}
        trend={trend}
        titleClassName={styles.metricTitle}
        TitleWrapper={TitleWrapper}
      />
    </Link>
  );
}

export function QualityMetricsWidget() {
  const isEnabled = useSelector(isQualityMetricsEnabled);
  const lang = useSelector(getLang('DASHBOARD'));
  const qualityMetricLang = useSelector(getLang('QUALITY_METRIC'));

  const { data, error } = useMyQualityMetrics({ enabled: isEnabled });
  const metrics = useMemo(() => data ?? [], [data]);

  const { ref } = useAnalyticsImpression({ event: EVENTS.QUALITY_METRICS.WIDGETS_IMPRESSION });
  const { userTrendsMap, sortedMetrics, comparisonLabel } = useMemo(
    () => deriveMetricsTrends(metrics),
    [metrics],
  );

  useEffect(() => {
    if (error) console.error('Error fetching metrics:', error);
  }, [error]);

  if (!metrics.length) {
    return null;
  }

  const displayMetrics = sortedMetrics.length ? sortedMetrics : metrics;
  const titleSuffix = comparisonLabel
    ? ` (${qualityMetricLang.TREND_VS.replace('{period}', comparisonLabel)})`
    : '';

  return (
    <div ref={ref}>
      <p className={styles.title}>
        {lang.TITLE_QUALITY_METRICS}
        {titleSuffix && <span className={styles.titleSuffix}>{titleSuffix}</span>}
      </p>

      <Divider className={styles.divider} />

      <GridItemsNavigator
        className={styles.items}
        columns={2}
        aria-label={lang.TITLE_QUALITY_METRICS}>
        {displayMetrics.map(metric => (
          <WidgetMetricBox key={metric.id} metric={metric} trend={userTrendsMap[metric.metricId]} />
        ))}
      </GridItemsNavigator>
    </div>
  );
}
